import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { BUTTON_QUIET, RequestList, RequestThread } from "../components/helpdesk/HelpDeskViews";
import { isStaff, useAuth } from "../lib/auth";
import {
  addReply,
  BUSINESS_HOURS_NOTE,
  FALLBACK_NOTE,
  friendlyError,
  isCompanyAdmin,
  listMessages,
  listTickets,
  markUrgent,
  RESPONSE_TARGETS,
  screenshotUrl,
} from "../lib/helpDesk/helpDesk.mjs";
import type { HelpMessage, HelpTicket } from "../lib/helpDesk/helpDesk.mjs";
import { getSupabase } from "../lib/supabase";
import { useTenantId } from "../lib/tenant";

/**
 * My requests (issue #1096): the help requests this session may read for the
 * tenant, their status and Meridian's replies, and a reply box. A team member
 * sees their own; the company's administrator (owner) can switch to every
 * request for the company and mark one urgent; staff see the tenant's all.
 * RLS (migration 0110) decides what comes back; the toggle only narrows it.
 */
export default function HelpRequests() {
  const tenantId = useTenantId();
  const { session } = useAuth();
  const userId = session?.user.id ?? null;
  const staff = isStaff(session);
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("request");

  const [admin, setAdmin] = useState(false);
  const [scope, setScope] = useState<"mine" | "company">("mine");
  const [tickets, setTickets] = useState<HelpTicket[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [shotHref, setShotHref] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    isCompanyAdmin(getSupabase(), tenantId).then((yes) => {
      if (!alive) return;
      setAdmin(yes);
      if (yes) setScope("company");
    });
    return () => {
      alive = false;
    };
  }, [tenantId]);

  const canSeeCompany = admin || staff;

  const load = useCallback(async () => {
    if (!tenantId) return;
    const { tickets: rows, error } = await listTickets(getSupabase(), {
      tenantId,
      userId,
      mineOnly: !(canSeeCompany && scope === "company"),
    });
    setLoadError(error ? friendlyError(error) : null);
    setTickets(rows);
  }, [tenantId, userId, canSeeCompany, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = tickets?.find((t) => t.id === selectedId) ?? null;

  const loadThread = useCallback(async () => {
    if (!tenantId || !selected) {
      setMessages([]);
      return;
    }
    const { messages: rows } = await listMessages(getSupabase(), {
      tenantId,
      ticketId: selected.id,
    });
    setMessages(rows);
  }, [tenantId, selected]);

  useEffect(() => {
    void loadThread();
  }, [loadThread]);

  const shotPath = selected?.screenshot_path ?? null;
  useEffect(() => {
    let alive = true;
    setShotHref(null);
    if (shotPath) {
      void screenshotUrl(getSupabase(), shotPath).then((url) => alive && setShotHref(url));
    }
    return () => {
      alive = false;
    };
  }, [shotPath]);

  function select(id: string) {
    const next = new URLSearchParams(params);
    next.set("request", id);
    setParams(next, { replace: true });
    setDraft("");
    setActionError(null);
  }

  async function sendReply() {
    if (!selected) return;
    setSending(true);
    setActionError(null);
    const { error } = await addReply(getSupabase(), { ticketId: selected.id, body: draft });
    setSending(false);
    if (error) {
      setActionError(friendlyError(error));
      return;
    }
    setDraft("");
    await load();
    await loadThread();
  }

  async function urgent() {
    if (!selected) return;
    setActionError(null);
    const { error } = await markUrgent(getSupabase(), selected.id);
    if (error) setActionError(friendlyError(error));
    await load();
  }

  return (
    <main style={{ padding: "24px 28px", display: "grid", gap: 16 }}>
      <header style={{ display: "grid", gap: 6 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>My requests</h1>
        <div style={{ fontSize: 13, color: "var(--text-2)" }}>
          Normal: {RESPONSE_TARGETS.normal.toLowerCase()}. Urgent:{" "}
          {RESPONSE_TARGETS.urgent.toLowerCase()}. {BUSINESS_HOURS_NOTE}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>{FALLBACK_NOTE}</div>
        {canSeeCompany ? (
          <div role="group" aria-label="Which requests" style={{ display: "flex", gap: 8 }}>
            {(["mine", "company"] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={scope === value}
                onClick={() => setScope(value)}
                style={{
                  ...BUTTON_QUIET,
                  padding: "4px 12px",
                  fontSize: 12,
                  background: scope === value ? "var(--domain-disposal-wash)" : "transparent",
                }}
              >
                {value === "mine" ? "Mine" : "Everyone at the company"}
              </button>
            ))}
          </div>
        ) : null}
      </header>

      {loadError ? (
        <div role="alert" style={{ color: "var(--flag-red)", fontSize: 13 }}>
          {loadError}
        </div>
      ) : null}

      {tickets == null ? (
        <div style={{ fontSize: 13, color: "var(--text-2)" }}>Loading...</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(240px, 360px) minmax(0, 1fr)",
            gap: 20,
            alignItems: "start",
          }}
        >
          <RequestList
            tickets={tickets}
            selectedId={selectedId}
            showRequester={canSeeCompany && scope === "company"}
            onSelect={select}
          />
          {selected ? (
            <RequestThread
              ticket={selected}
              messages={messages}
              replyDraft={draft}
              sending={sending}
              error={actionError}
              canMarkUrgent={canSeeCompany}
              screenshotHref={shotHref}
              onReplyChange={setDraft}
              onSendReply={() => void sendReply()}
              onMarkUrgent={() => void urgent()}
            />
          ) : (
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>
              {tickets.length ? "Pick a request to see the replies." : ""}
            </div>
          )}
        </div>
      )}
    </main>
  );
}
